import React, { useEffect, useState, useMemo, useRef } from "react";
import * as actions from "../Common/actions";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import { Link } from "react-router-dom";
import SelectCMP from "react-select";
import { ticketOptions } from "../../constants";
import { Ticket, User, Mail, MessageSquare, Calendar, Copy, ClipboardCheck, RefreshCw } from 'lucide-react';
import { toastError, copyToClipboard } from "../../utils";
import SearchInput from "../../components/SearchInput";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import darkSelectStyles from "../../theme/reactSelectStyles";

const CACHE_KEY = 'ticketsCache'
const CACHE_TTL = 5 * 60 * 1000

function getCachedData() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw)
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    return cached
  } catch { return null }
}

function setCachedData(body) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      body,
      timestamp: Date.now()
    }))
  } catch { }
}

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Tickets(props) {
  const cached = getCachedData()
  const [tickets, setTickets] = useState(cached?.body || []);
  const [searchText, setSearchText] = useState("");
  const [bgLoading, setBgLoading] = useState(false);
  const refreshTimerRef = useRef(null);
  const [selectedTicketType, setSelectedTicketType] = useState({
    label: "Open",
    value: "open",
  });

  const onGetAllTickets = (filters = {}, silent = false) => {
    const hasCached = getCachedData()
    if (hasCached && !silent) {
      setBgLoading(true)
    } else {
      props.setLoader(true);
    }
    actions
      .getTicketListForAdmin({ ...filters, sort: filters.sort || selectedTicketType.value })
      .then((res) => {
        props.setLoader(false);
        setBgLoading(false);
        setTickets(res.body);
        setCachedData(res.body);
      })
      .catch((err) => {
        props.setLoader(false);
        setBgLoading(false);
        toastError(err?.message || 'Failed to load tickets');
      });
  };

  const initialLoadRef = useRef(true);

  useEffect(() => {
    document.title = 'TK | Tickets';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    onGetAllTickets();
  }, []);

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    sessionStorage.removeItem(CACHE_KEY);
    onGetAllTickets({ sort: selectedTicketType.value });
  }, [selectedTicketType]);

  // Auto-refresh cache every 5 minutes
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      onGetAllTickets({}, false)
    }, CACHE_TTL)
    return () => clearInterval(refreshTimerRef.current)
  }, [])

  // Client-side search filtering
  const isSearching = searchText.trim().length >= 3
  const filteredTickets = useMemo(() => {
    if (isSearching) {
      const q = searchText.toLowerCase()
      return tickets.filter(item =>
        (item.user_name && item.user_name.toLowerCase().includes(q)) ||
        (item.user_email && item.user_email.toLowerCase().includes(q)) ||
        (item.reason && item.reason.toLowerCase().includes(q)) ||
        (item.ticket_id && String(item.ticket_id).includes(q))
      )
    }
    return tickets
  }, [tickets, searchText])

  const renderTicketCard = (item) => (
    <Paper
      key={item.ticket_id}
      className="approved-release-card"
      style={{ cursor: 'pointer', borderLeft: `3px solid ${item.status === 0 ? '#10B981' : '#F59E0B'}` }}
      component={Link}
      to={`/view_tickets/${item.ticket_id}`}
      sx={{ textDecoration: 'none', backgroundColor: item.status === 0 ? 'rgba(16, 185, 129, 0.04)' : 'rgba(245, 158, 11, 0.04)' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <Ticket size={20} color="#64748B" />
          <span>Ticket</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.reason || 'No Reason'}
            </Typography>
          </div>
          <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <StatusBadge status={item.status === 0 ? 'open' : 'closed'} />
          </div>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">TKT-{item.ticket_id}</span>
          <CopyMeta icon={<User size={13} />} label="Name" value={item.user_name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.user_email} />
          {item.date && (
            <span className="approved-release-card__meta-item" style={{ color: '#64748B' }}>
              <Calendar size={13} /> {moment(item.date).format('DD/MM/YYYY')}
            </span>
          )}
        </div>
      </div>
    </Paper>
  )

  return (
    <Grid container>
      {/* ========== Header ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
          <Box sx={{ maxWidth: 420, flex: 1 }}>
            <SearchInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by name, email, reason, or ticket ID..."
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
            {bgLoading && (
              <Box display="flex" alignItems="center" gap={0.75} sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                <RefreshCw size={13} className="spin-icon" /> Refreshing...
              </Box>
            )}
            <Box sx={{ minWidth: 130 }}>
              <SelectCMP
                options={ticketOptions}
                value={selectedTicketType}
                onChange={(e) => setSelectedTicketType(e)}
                styles={darkSelectStyles}
              />
            </Box>
            <Chip
              icon={<Ticket size={14} />}
              label={`${tickets.length} ${selectedTicketType.label}`}
              sx={{
                backgroundColor: selectedTicketType.value === 'open' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(108, 99, 255, 0.12)',
                color: selectedTicketType.value === 'open' ? '#F59E0B' : '#6C63FF',
                border: `1px solid ${selectedTicketType.value === 'open' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(108, 99, 255, 0.25)'}`,
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: selectedTicketType.value === 'open' ? '#F59E0B' : '#6C63FF' },
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* ========== Ticket Cards ========== */}
      <Grid item xs={12}>
        <Box className="approved-release-list">
          {filteredTickets.length ? (
            filteredTickets.map((item) => renderTicketCard(item))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Tickets Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
